import { type ChangeEvent, useState } from 'react';
import type { Api } from '../api.js';
import { type Account, TYPE_LABELS } from './types.js';
import { useOrgData } from './useOrgData.js';

interface Proposal {
  code: string;
  name: string;
  type: Account['type'];
  systemRole: string;
  defaultTaxCode?: string;
}
interface Missing {
  role: string;
  proposal: Proposal | null;
}
interface ImportResult {
  imported: number;
  warnings: string[];
  review: { code: string; name: string; review: string }[];
}

const ROLE_LABELS: Record<string, string> = {
  gst_control: 'GST control',
  suspense: 'Suspense',
  retained_earnings: 'Retained earnings',
  stripe_clearing: 'Stripe clearing',
  director_loan: 'Director loan (Division 7A)',
  ato_clearing: 'ATO activity statement',
  payg_instalments: 'PAYG instalments payable',
  income_tax_payable: 'Income tax payable',
  deferred_revenue: 'Deferred revenue',
  stripe_fees: 'Payment processing (Stripe) fees',
  fx: 'Foreign exchange gains and losses',
};

export function ChartOfAccounts({ api, orgId }: { api: Api; orgId: string }) {
  const { data, error, reload } = useOrgData<{ accounts: Account[] }>(api, orgId, '/accounts');
  const missing = useOrgData<{ missing: Missing[] }>(api, orgId, '/accounts/missing-roles');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setActionError('');
    try {
      await action();
      await Promise.all([reload(), missing.reload()]);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function importFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const csv = await file.text();
    await run(async () =>
      setResult(await api.send<ImportResult>(orgId, 'POST', '/accounts/import-xero', { csv })),
    );
  }

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading</p>;

  if (data.accounts.length === 0) {
    return (
      <section className="stack">
        <h2>Chart of accounts</h2>
        <p>
          Import the chart from Xero (Accounting, Chart of accounts, Export), or start from the
          standard Australian small-company chart.
        </p>
        <label>
          Xero chart of accounts CSV
          <input type="file" accept=".csv,text/csv" onChange={importFile} disabled={busy} />
        </label>
        <button
          type="button"
          className="link"
          onClick={() => run(() => api.send(orgId, 'POST', '/accounts/template'))}
          disabled={busy}
        >
          Use the standard chart instead
        </button>
        {actionError && <p className="error">{actionError}</p>}
      </section>
    );
  }

  const groups = (Object.keys(TYPE_LABELS) as Account['type'][]).map((type) => ({
    type,
    accounts: data.accounts.filter((a) => a.type === type),
  }));
  const reviewByCode = new Map(result?.review.map((r) => [r.code, r.review]));
  const gaps = missing.data?.missing ?? [];

  return (
    <section className="stack">
      <h2>Chart of accounts</h2>
      {result && (
        <p>
          Imported {result.imported} accounts from Xero.
          {result.warnings.map((w) => (
            <span key={w} className="error">
              {' '}
              {w}
            </span>
          ))}
        </p>
      )}
      {gaps.length > 0 && (
        <div className="notice stack">
          <strong>Foderis needs these accounts</strong>
          {gaps.map((g) => (
            <div key={g.role} className="title-row">
              <span>
                {ROLE_LABELS[g.role] ?? g.role}
                {g.proposal && (
                  <span className="muted">
                    {' '}
                    : add {g.proposal.code} {g.proposal.name}
                  </span>
                )}
              </span>
              {g.proposal && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => api.send(orgId, 'POST', '/accounts', g.proposal))}
                >
                  Add
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {actionError && <p className="error">{actionError}</p>}
      {groups.map((g) => (
        <table key={g.type} className="table">
          <caption>{TYPE_LABELS[g.type]}</caption>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Default tax code</th>
              <th>Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {g.accounts.map((a) => (
              <tr key={a.id} className={a.archived ? 'muted' : undefined}>
                <td>{a.code}</td>
                <td>
                  {a.name}
                  {reviewByCode.has(a.code) && (
                    <div className="muted">{reviewByCode.get(a.code)}</div>
                  )}
                </td>
                <td>{a.default_tax_code?.replace(/^AU:/, '') ?? ''}</td>
                <td className="muted">
                  {a.system_role ? (ROLE_LABELS[a.system_role] ?? a.system_role) : ''}
                </td>
                <td>
                  {!a.system_role && (
                    <button
                      type="button"
                      className="link"
                      disabled={busy}
                      onClick={() =>
                        run(() =>
                          api.send(orgId, 'PATCH', `/accounts/${a.id}`, { archived: !a.archived }),
                        )
                      }
                    >
                      {a.archived ? 'Restore' : 'Archive'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </section>
  );
}
